import React, { useState } from "react";
import {
  Box,
  Text,
  VStack,
  HStack,
  Image,
  IconButton,
  Spacer,
  useColorMode,
  Button,
  useToast,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
} from "@chakra-ui/react";
import { useHistory } from "react-router-dom";
import { FaTrash, FaBus, FaHeart, FaPen } from "react-icons/fa";
import { CloseIcon, ChevronDownIcon } from "@chakra-ui/icons";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import axiosInstance from "../../helpers/axiosInstance";
import GridIcon from "../../assets/icons/dot_grid_icon.svg";
import { useCurrency } from "../CurrencyExchange/CurrencyContext";
import Translate from "../../components/LanguageTranslation/Translate";
import AddBusTicketItemModal from "./AddBusTicketItemModal";

const BusTicketList = ({
  busTickets,
  setBusTickets,
  tripId,
  location,
  setView,
  handleSuggestionClick,
  isAddBusTicketOpen,
  setIsAddBusTicketOpen,
  city,
}) => {
  // console.log("busTickets", busTickets);
  const { colorMode } = useColorMode();
  const history = useHistory();
  const toast = useToast();
  const [hoveredItem, setHoveredItem] = useState(null);
  const { convertCurrency, currency } = useCurrency();

  const handleCloseDetail = () => {
    setView(null);
  };

  // Drag and drop handler
  const handleOnDragEnd = async (result) => {
    if (!result.destination) return;
    const items = Array.from(busTickets);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    setBusTickets(items);
    // Payload with updated bus tickets list
    const payload = {
      bus_api_response: items,
      city,
    };
    try {
      await axiosInstance.put(
        `/bucket-list/${tripId}/update/bus`,
        payload
      );
    } catch (error) {
      console.error("Error updating bus ticket order:", error);
      toast({
        title: "Error updating order",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Delete handler
  const handleBusTicketDelete = async (busTicket) => {
    // console.log("handleBusTicketDelete",busTicket)
    const updatedBusTickets = busTickets.filter((t) => t !== busTicket);
    setBusTickets(updatedBusTickets);
    const payload = {
      bus_api_response: updatedBusTickets,
      city,
    };
    try {
      const response = await axiosInstance.put(
        `/bucket-list/${tripId}/update/bus`,
        payload
      );
      if (response.status === 200) {
        toast({
          title: "Bus ticket removed successfully!",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error("Error deleting bus ticket:", error);
      toast({
        title: "Error deleting bus ticket",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Format ISO date to readable
  const formatDate = (iso) => (iso ? new Date(iso).toLocaleDateString() : "—");

  return (
    <Box
      bg={colorMode === "dark" ? "gray.800" : "blackAlpha.50"}
      w="100%"
      h="100vh"
      overflowY="hidden"
    >
      <HStack justifyContent="space-between" bg="white">
        <Box
          flex="1"
          display="flex"
          justifyContent="left"
          p={4}
          pt={2}
          pb={2}
          pl={4}
          maxH="8vh"
          borderBottom="1px"
          borderLeft="1px"
          borderColor="blackAlpha.200"
        >
          <Text
            fontSize="md"
            fontWeight="bold"
            mt={1}
            pb={2}
            color="blackAlpha.700"
          >
            <Translate text="Update Bus Tickets" />
          </Text>
          <Spacer />
          <Menu>
            <MenuButton
              as={Button}
              bg="#1A202C"
              color="white"
              _hover={{ bg: "#1A202C" }}
              _active={{ bg: "#1A202C" }}
              size={{ base: "xs", md: "sm", lg: "sm" }}
              rightIcon={<ChevronDownIcon />}
              mr={4}
            >
              <Translate text="Update Bus Tickets" />
            </MenuButton>
            <MenuList>
              <MenuItem
                onClick={() => setIsAddBusTicketOpen(true)}
                fontSize={{ base: "xs", md: "sm", lg: "sm" }}
              >
                <Translate text="Add Bucket List Items" />
              </MenuItem>
            </MenuList>
          </Menu>
          <IconButton
            icon={<CloseIcon boxSize={2.5} />}
            onClick={handleCloseDetail}
            colorScheme="gray"
            size="sm"
            isRound={true}
            position="relative"
            zIndex="tooltip"
            bg="white"
            boxShadow="md"
          />
        </Box>
      </HStack>
      <Box h={{ base: "91vh", md: "89vh", lg: "89vh" }}>
        <DragDropContext onDragEnd={handleOnDragEnd}>
          <Droppable droppableId="busTickets">
            {(provided) => (
              <VStack
                spacing={4}
                h="100%"
                overflowY="auto"
                sx={{
                  "&::-webkit-scrollbar": {
                    display: "none",
                  },
                  msOverflowStyle: "none",
                  scrollbarWidth: "none",
                }}
                bg="white"
                align="stretch"
                p={4}
                m={2}
                {...provided.droppableProps}
                ref={provided.innerRef}
              >
                {busTickets &&
                  busTickets.length > 0 &&
                  busTickets.map((busTicket, index) => {
                    // Safely handle missing price data
                    const priceAmount = busTicket.price?.amount ?? 0;
                    const priceCurrency = busTicket.price?.currency ?? "USD";
                    const convertedPrice = convertCurrency(
                      priceAmount,
                      priceCurrency
                    );
                    return (
                      <Draggable
                        key={index}
                        draggableId={`${index}`}
                        index={index}
                      >
                        {(provided) => (
                          <Box
                            position="relative"
                            bg="white"
                            borderRadius="lg"
                            boxShadow="md"
                            border="1px solid"
                            borderColor="gray.200"
                            display="flex"
                            flexDirection="row"
                            alignItems="center"
                            justifyContent="space-between"
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            onMouseEnter={() => setHoveredItem(index)}
                            onMouseLeave={() => setHoveredItem(null)}
                          >
                            <Box
                              position="absolute"
                              top="-10px"
                              left="-10px"
                              bg="black"
                              color="white"
                              borderRadius="full"
                              width="24px"
                              height="24px"
                              display="flex"
                              alignItems="center"
                              justifyContent="center"
                              fontSize="12px"
                              fontWeight="bold"
                            >
                              {index + 1}
                            </Box>
                            <Box
                              {...provided.dragHandleProps}
                              bg="gray.100"
                              alignContent="center"
                              visibility={
                                hoveredItem === index ? "visible" : "hidden"
                              }
                              h="100%"
                            >
                              <Image
                                src={GridIcon}
                                alt="Drag Handle"
                                boxSize="4"
                                opacity={hoveredItem === index ? 1 : 0.5}
                              />
                            </Box>
                            <VStack ml={1} w="full" p={4} pl={0} h="100%">
                              <HStack align="center" spacing={4} w="100%">
                                <VStack
                                  align="flex-start"
                                  spacing={1}
                                  flex="1"
                                  h="100%"
                                >
                                  <Text
                                    fontSize="18px"
                                    fontWeight="bold"
                                    mb={0}
                                  >
                                    <Translate
                                      text={
                                        busTicket.bus_company || "Bus Company"
                                      }
                                    />
                                  </Text>
                                  <Text fontSize="14px" color="gray.600" mb={0}>
                                    {currency} {convertedPrice}
                                  </Text>
                                </VStack>
                              </HStack>
                              <HStack
                                justify="space-between"
                                width="full"
                                spacing={0}
                                bg="blackAlpha.50"
                                padding={2}
                                borderRadius="xl"
                              >
                                <Text fontSize="sm" mb={0}>
                                  {busTicket.origin_station}
                                </Text>
                                <Text fontSize="lg" mx={2} mb={0}>
                                  ➔
                                </Text>
                                <Text fontSize="sm" mb={0}>
                                  {busTicket.destination_station}
                                </Text>
                              </HStack>
                              <HStack
                                justify="space-between"
                                mb={2}
                                width="100%"
                              >
                                <Text fontSize="sm" mb={0}>
                                  <Translate text="Departure:" />{" "}
                                  {formatDate(busTicket.departure_date)}
                                </Text>
                                <Text fontSize="sm" mb={0}>
                                  <Translate text="Arrival:" />{" "}
                                  {formatDate(busTicket.arrival_date)}
                                </Text>
                              </HStack>
                              <HStack spacing={1} w="100%">
                                <Spacer />
                                <IconButton
                                  icon={<FaTrash />}
                                  aria-label="Delete RV"
                                  borderRadius="full"
                                  size="xs"
                                  visibility={
                                    hoveredItem === index ? "visible" : "hidden"
                                  }
                                  onClick={() =>
                                    handleBusTicketDelete(busTicket)
                                  }
                                />
                              </HStack>
                            </VStack>
                          </Box>
                        )}
                      </Draggable>
                    );
                  })}
                {provided.placeholder}
              </VStack>
            )}
          </Droppable>
        </DragDropContext>
      </Box>
      <AddBusTicketItemModal
        isOpen={isAddBusTicketOpen}
        onClose={() => setIsAddBusTicketOpen(false)}
        tripId={tripId}
        busTickets={busTickets}
        setBusTickets={setBusTickets}
        city={city}
      />
    </Box>
  );
};

export default BusTicketList;
